import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import PrimeDialog from "../../components/dialog/PrimeDialog";
import { FormInput } from "../../components/FormInput";
import { FormInputFile } from "../../components/FormInputFile";
import FormNumberInput from "../../components/FormNumberInput";
import ImagePDFComponent from "../../components/ImagePDFComponent";
import PanelDataTable from "../../components/PanelDataTable";
import { useEditableFlag } from "../../hooks/useEditableFlag";
import CarbonCoefficientComponent from "../../pages/inventoryCarbonFootprint/cusComponents/CarbonCoefficientComponent";
import { SystemLoading, checkLoading } from "../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../store/messageStore";
import { AlertService } from "../../utility/service/alert-service";
import {
  F1060103Before,
  F1060104Before,
  createEquipmentProcess,
  queryEquipmentProcessDetail,
  updateEquipmentProcess,
} from "../../utility/service/cbam-service";
import { getCBAMSubItemsByItemNo } from "../../utility/service/companyOffering-service";
import {
  Base64Encoder,
  BcryptEncoder,
  checkDpSuccess,
  getFileBase64,
  splitFileExtension,
  splitFileName,
} from "../../utility/service/tool-service";
import { addFormValidator } from "../../utility/validate/formValidator";

export default function EquipmentProcessSettingsDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formRule, setFormRule] = useState([]); //表單驗證 設備
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const {
    action,
    setAction,
    itemObj,
    setItemObj,
    selectPlaceList,
    setSelectPlaceList,
    currentCompanyName,
  } = useOutletContext();
  const [, loading] = useContext(SystemLoading);
  const [currentItem, setCurrentItem] = useState({});
  const [placeName, setPlaceName] = useState("");
  // 下拉選單
  const [emissionTypeList, setEmissionTypeList] = useState([]);
  const [unitList, setUnitList] = useState([]);
  // 附件
  const fileInputRef = useRef(null);
  const [fileChanged, setFileChanged] = useState(false);
  const [delFillChanged, setDelFillChanged] = useState(false);
  const [fileInfo, setFileInfo] = useState({});
  const [zoomSrc, setZoomSrc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  // 排放係數popup
  const [showIndex, setShowIndex] = useState(false);
  const [currentRowIndex, setCurrentRowIndex] = useState(null);
  const isEditable = useEditableFlag("F10601");
  const [tabAction, setTabAction] = useState("detail");
  const editFlag = tabAction === "detail" || !isEditable;
  const [oriData, setOriData] = useState({});

  const {
    getValues,
    setValue,
    watch,
    control,
    register,
    trigger,
    handleSubmit,
    clearErrors,
    setError,
    reset,
    resetField,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {},
    //錯誤驗證時機
    mode: "all",
    criteriaMode: "all",
  });

  // 設置標記，表示從子頁面返回
  useEffect(() => {
    sessionStorage.setItem("fromEquipmentProcessSettingsChildPage", "true");
  }, []);

  useEffect(() => {
    const processConstraints = (constraints) => {
      return constraints.map((item) => {
        if (item?.subObject?.value) {
          return { [item?.field]: processConstraints(item?.subObject.value) };
        } else {
          return addFormValidator(item?.field, constraints);
        }
      });
    };

    const handleCreate = async () => {
      try {
        const res = await F1060103Before();
        if (checkDpSuccess(res)) {
          setFormRule(processConstraints(res?.data?.RespBody?.constraints));
        }
      } finally {
        addForm({ equipmentDetailId: "" });
      }
    };

    const handleEdit = async () => {
      const res = await F1060104Before();
      if (checkDpSuccess(res)) {
        setFormRule(processConstraints(res?.data?.RespBody?.constraints));
      }
      axios_queryEquipmentProcessDetail();
    };

    if (action === "create") {
      handleCreate();
      setTabAction("create");
    } else if (action === "detail") {
      handleEdit();
    } else {
      btnBackTo();
    }

    axios_getCBAMSubItemsByItemNo("CBAM_EMISSION_TYPE", 4).then(setEmissionTypeList);
    axios_getCBAMSubItemsByItemNo("AD_UNIT", 13).then(setUnitList);
  }, []);

  const btnBackTo = () => {
    navigate("/management/equipmentProcessSettings");
  };

  // 製成設備詳情查詢
  const axios_queryEquipmentProcessDetail = () => {
    checkLoading(loading);
    queryEquipmentProcessDetail({
      equipmentId: itemObj?.equipmentId,
    })
      .then((res) => {
        if (checkDpSuccess(res)) {
          const data = res.data.RespBody;
          setCurrentItem(data);
          setPlaceName(data?.placeName || "");
          setDefaultValue(data);
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  const setDefaultValue = (data) => {
    setOriData(data);
    const fieldsToSet = {
      processName: data?.processName,
      equipmentName: data?.equipmentName,
      placeSeq: data?.placeSeq,
      equipmentDetailList: data?.equipmentDetailList,
      directEmissions: data?.directEmissions,
      indirectEmissions: data?.indirectEmissions,
    };

    if (data?.fileId) {
      Object.assign(fieldsToSet, {
        fileId: data?.fileId,
        fileName: data?.fileName,
        fileExtension: data?.fileExtension,
        filePic: data?.filePathSml,
      });
    }

    Object.entries(fieldsToSet).forEach(([key, value]) => {
      setValue(key, value);
    });

    if (data?.fileExtension) {
      setZoomSrc(data?.filePath);
      setPreviewUrl(data?.fileExtension === "pdf" ? data?.filePath : data?.filePathSml);
      setFileInfo({
        type: data?.fileExtension,
        name: `${data?.fileName}.${data?.fileExtension}`,
      });
    } else {
      setZoomSrc("");
      setPreviewUrl("");
      setFileInfo({});
    }
  };

  // CBAM 下拉選單
  const axios_getCBAMSubItemsByItemNo = (itemNo, orderNo) => {
    return getCBAMSubItemsByItemNo(itemNo, orderNo).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        return data.map((item, index) => ({
          ...item,
          index,
        }));
      }
      return [];
    });
  };

  const setValidForm = useCallback(
    (formId) => {
      if (formRule?.length > 0) {
        const tmp = formRule?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [formRule],
  );

  const setValidDetailForm = useCallback(
    (formId) => {
      if (setValidForm("equipmentDetailList")?.length > 0) {
        const tmp = setValidForm("equipmentDetailList")?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [setValidForm("equipmentDetailList")],
  );

  // 上傳檔案
  const handleFileChange = (e) => {
    //每次開啟選擇檔案觸發onChange事件
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size >= 5000000) {
      AlertService.ok("Error", "Size over 5MB");
      setPreviewUrl(null);
      setZoomSrc(null);
      setValue("fileExtension", "");
      setValue("fileName", "");
      setValue("filePic", "");
      setFileInfo({});
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
      return;
    }

    const fileURL = URL.createObjectURL(file);
    setPreviewUrl(fileURL);
    setZoomSrc(fileURL);

    const fileExtension = splitFileExtension(file.name);
    const fileName = splitFileName(file.name);

    setValue("fileExtension", fileExtension);
    setValue("fileName", fileName);
    setFileInfo(file);

    // 轉 base64 並更新表單
    getFileBase64(file).then((result) => {
      setValue("filePathSml", result);
      setValue("filePath", result);
    });

    setValue("filePic", file, { shouldValidate: true, shouldDirty: true }); //檔案格式 原圖 如果轉為MulitPart 需解開此
    setFileChanged(true);
  };

  // 刪除檔案
  const handleDeleteImg = () => {
    if (fileInputRef?.current) {
      fileInputRef.current.value = null;
    }
    setPreviewUrl(null);
    setZoomSrc(null);
    setValue("fileExtension", "");
    setValue("fileName", "");
    setValue("filePic", "", { shouldValidate: true, shouldDirty: true });

    // 測試用 若不需要可移除
    setValue("filePathSml", "");
    setValue("filePath", "");

    setDelFillChanged(true);
  };

  const defaultEquipmentDetailList = {
    equipmentDetailId: "",
  };

  const { fields: equipmentDetailFields, append: addForm, remove: delForm } = useFieldArray({
    control,
    name: "equipmentDetailList",
  });

  const handleResetForm = (fieldArrayApi, defaultList, index) => {
    clearErrors();
    fieldArrayApi.remove(index);
    fieldArrayApi.append(defaultList);
  };

  const handleDelForm = (fieldArrayApi, defaultList, index, length) => {
    clearErrors();
    fieldArrayApi.remove(index);
    if (length <= 1) {
      fieldArrayApi.append(defaultList);
    }
  };

  const panelConfig = {
    header: t("rawMaterial"),
  };

  const dataTableConfig = {
    dataKey: "id",
    value: equipmentDetailFields,
    className: `${editFlag  ? "last-row-no-border" : ""}`,
    footerEvent: () => addForm(defaultEquipmentDetailList),
    footerDisabled: editFlag,
  };

  const columnsConfig = [
    // 排放類別
    {
      field: "emissionType",
      fieldName: "emissionType",
      fieldListName: "equipmentDetailList",
      header: t("emissionCategory"),
      headerRequired: tabAction !== "detail",
      type: "dropdown",
      control: control,
      rules: setValidDetailForm("emissionType"),
      options: emissionTypeList,
      optionLabel: "subitemName",
      optionValue: "subitemNo",
      placeholder: t("plz_chs"),
      disabled: editFlag,
    },
    // 排放係數名稱
    {
      field: "sourceSeq",
      fieldName: "sourceSeq",
      fieldListName: "equipmentDetailList",
      fieldBodyName: "sourceName",
      header: t("emissionFactor"),
      headerRequired: tabAction !== "detail",
      type: "search",
      control: control,
      rules: setValidDetailForm("sourceSeq"),
      register: register,
      errors: errors,
      btnSerch: (rowIndex) => {
        setCurrentRowIndex(rowIndex);
        setShowIndex(true);
      },
      disabled: editFlag,
      disabledBtn: editFlag,
    },
    // 每公噸產品能耗
    {
      field: "consumptionPerTon",
      fieldName: "consumptionPerTon",
      fieldListName: "equipmentDetailList",
      header: t("energyPerTonProduct"),
      headerRequired: tabAction !== "detail",
      type: "number",
      control: control,
      rules: setValidDetailForm("consumptionPerTon"),
      disabled: editFlag,
    },
    // 單位
    {
      field: "consumptionUnit",
      fieldName: "consumptionUnit",
      fieldListName: "equipmentDetailList",
      header: t("unit"),
      headerRequired: tabAction !== "detail",
      type: "dropdown",
      control: control,
      rules: setValidDetailForm("consumptionUnit"),
      options: unitList,
      optionLabel: "subitemName",
      optionValue: "subitemNo",
      placeholder: t("plz_chs"),
      disabled: editFlag,
    },
    // 轉換率
    {
      field: "conversionRate",
      fieldName: "conversionRate",
      fieldListName: "equipmentDetailList",
      header: t("conversionRate"),
      type: "number",
      control: control,
      rules: setValidDetailForm("conversionRate"),
      disabled: editFlag,
    },
  ];

  const actionConfig = {
    fields: equipmentDetailFields,
    fieldId: "id",
    handleDelForm: handleDelForm,
    handleResetForm: handleResetForm,
    fieldArrayApi: { remove: delForm, append: addForm },
    defaultList: defaultEquipmentDetailList,
    disabled: editFlag,
  };

  const handlePickData = (rowData) => {
    setShowIndex(false);
    const fieldPrefix = `equipmentDetailList[${currentRowIndex}]`;

    setValue(`${fieldPrefix}.sourceName`, rowData?.coefficientName, { shouldValidate: true, shouldDirty: true });
    setValue(`${fieldPrefix}.sourceType`, "1", { shouldValidate: true, shouldDirty: true });
    setValue(`${fieldPrefix}.sourceSeq`, rowData?.coefficientId, { shouldValidate: true, shouldDirty: true });

    setShowIndex(false); // 關閉 dialog
    setCurrentRowIndex(null); // 清空索引
  };

  const findItemIndex = (array, code) => {
    let result = array?.filter((item) => item.subitemNo === code);
    return result.length > 0 ? result[0].index : false;
  };

  const encoder = (dataList, code) => {
    if (code === null || code === undefined || code === "") return "";
    if (dataList === null || dataList === undefined || dataList === "" || dataList.length <= 0) return "";
    return Base64Encoder(BcryptEncoder(code ?? "")) + "," + findItemIndex(dataList, code ?? "");
  };

  const onSubmit = async (data) => {
    checkLoading(loading);

    await new Promise((resolve) => setTimeout(resolve, 0));

    if (data?.equipmentDetailList) {
      await Promise.all(
        data.equipmentDetailList.map(async (item) => {
          item.emissionType = encoder(emissionTypeList, item.emissionType);
          item.consumptionUnit = encoder(unitList, item.consumptionUnit);
        }),
      );
    }

    let formData = new FormData();
    formData.append("companySeq", selectPlaceList?.companySeq);
    formData.append("placeSeq", data?.placeSeq);
    formData.append("placeName", placeName);
    formData.append("processName", data?.processName);
    formData.append("equipmentName", data?.equipmentName);
    formData.append("directEmissions", data?.directEmissions);
    formData.append("indirectEmissions", data?.indirectEmissions);
    formData.append("equipmentDetailList", JSON.stringify(data?.equipmentDetailList));

    const fileExtension = getValues("fileExtension") || null;
    const fileName = getValues("fileName") || null;
    const filePic = getValues("filePic") || null;

    // 刪除
    if (currentItem?.fileId != null && filePic === null && delFillChanged) {
      formData.append("fileId", currentItem?.fileId);
    } else if (currentItem?.fileId == null && filePic !== null && fileChanged) {
      // 新增
      formData.append("filePic", filePic);
      formData.append("fileName", fileName);
      formData.append("fileExtension", fileExtension);
    } else if (filePic !== null && fileChanged) {
      // 更新
      formData.append("fileId", currentItem?.fileId);
      formData.append("filePic", filePic);
      formData.append("fileName", fileName);
      formData.append("fileExtension", fileExtension);
    }

    if (action == "update") {
      formData.append("equipmentId", itemObj?.equipmentId);
    }

    let apiCall = action === "create" ? createEquipmentProcess(formData) : updateEquipmentProcess(formData);

    apiCall
      .then((res) => {
        if (checkDpSuccess(res)) {
          handleSuccessMessage(dispatch, t(`button.${action}`) + " " + t("message.success"));
          btnBackTo();
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  return (
    <>
      {/* 上方基本資訊 */}
      <div className="flex justify-content-between flex-wrap align-items-center">
        <div className="align-items-start">
          <Button
            rounded
            text
            onClick={() => {
              btnBackTo();
            }}
          >
            <FontAwesomeIcon icon="fa-solid fa-arrow-left-long" />
            {t("button.return_to_list")}
          </Button>
        </div>
        <div className="align-items-end">{t(`button.${action}`)}</div>
      </div>
      {currentCompanyName && (
        <div className="p-card bg-gray">
          <div className="p-card-body">
            <div className="grid">
              <div className="col-12">
                <h3 className="m-0">{currentCompanyName}</h3>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="p-card">
        <div className="p-card-body">
          <form className="grid" onSubmit={handleSubmit(onSubmit)}>
            <div className="grid col-12 lg:col-6">
              {/* 製程 */}
              <div className="col-12 lg:col-6">
                <FormInput
                  disabled={editFlag}
                  register={register}
                  errors={errors}
                  labelClass={`${tabAction === "detail" ? "" : "required"}`}
                  id="processName"
                  name="processName"
                  type="text"
                  labelText={t("processName")}
                  rules={setValidForm("processName")}
                />
              </div>
              {/* 設備 */}
              <div className="col-12 lg:col-6">
                <FormInput
                  disabled={editFlag}
                  register={register}
                  errors={errors}
                  labelClass={`${tabAction === "detail" ? "" : "required"}`}
                  id="equipmentName"
                  name="equipmentName"
                  type="text"
                  labelText={t("equipmentName")}
                  rules={setValidForm("equipmentName")}
                />
              </div>
              {/* 場所 */}
              <div className="col-12 lg:col-6">
                <label htmlFor="place" className={`form-label ${tabAction === "detail" ? "" : "required"}`}>
                  {t("place")}
                </label>
                <Controller
                  name="placeSeq"
                  control={control}
                  rules={setValidForm("placeSeq")}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      disabled={editFlag}
                      appendTo={"self"}
                      optionLabel="placeName"
                      optionValue="placeSeq"
                      value={field.value}
                      virtualScrollerOptions={{ itemSize: 40 }}
                      defaultValue={""}
                      placeholder={t("plz_chs")}
                      name="place"
                      control={control}
                      options={selectPlaceList?.placeList || []}
                      className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                      onChange={(event) => {
                        field.onChange(event.value);
                        let place = selectPlaceList?.placeList?.filter((item) => item.placeSeq === event.value)[0];
                        setPlaceName(place.placeName);
                      }}
                    ></Dropdown>
                  )}
                ></Controller>
              </div>
            </div>
            {/* 檔案上傳區 */}
            <div className="col-12 lg:col-6">
              <label htmlFor="filePic" className="form-label">
                {t("attachments")}
              </label>
              <div className="grid">
                {!editFlag && (
                  <div className={`col-12 ${previewUrl ? "lg:col-8" : ""}`}>
                    <FormInputFile
                      register={register}
                      errors={errors}
                      acceptFileType=".jpg,.png,.pdf"
                      id="filePic"
                      name="filePic"
                      fileInputRef={fileInputRef}
                      fileChange={(e) => {
                        handleFileChange(e);
                      }}
                    />
                  </div>
                )}
                <div className={`col-12 ${previewUrl ? "lg:col-4" : ""}`}>
                  <ImagePDFComponent
                    disabled={editFlag}
                    file={fileInfo}
                    previewUrl={previewUrl}
                    zoomSrc={zoomSrc}
                    imgWidth="150"
                    imgHeight="150"
                    deleteImg={handleDeleteImg}
                  />
                </div>
              </div>
            </div>
            {/* 原然物料 */}
            <div className="col-12">
              <PanelDataTable
                panelConfig={panelConfig}
                dataTableConfig={dataTableConfig}
                columnsConfig={columnsConfig}
                actionConfig={actionConfig}
              />
            </div>
            <div className="step-content col-12">&nbsp;</div>
            {/* 直接排放 */}
            <div className="col-12 lg:col-6">
              <FormNumberInput
                disabled={editFlag}
                register={register}
                errors={errors}
                labelClass={`${tabAction === "detail" ? "" : "required"}`}
                id="directEmissions"
                name="directEmissions"
                step="any"
                labelText={`${t("directEmissions")}(tCO₂e/t)`}
                rules={setValidForm("directEmissions")}
                value={watch("directEmissions")}
                onChange={(newValue) => {
                  setValue("directEmissions", newValue);
                }}
              />
            </div>
            {/* 間接排放 */}
            <div className="col-12 lg:col-6">
              <FormNumberInput
                disabled={editFlag}
                register={register}
                errors={errors}
                labelClass={`${tabAction === "detail" ? "" : "required"}`}
                id="indirectEmissions"
                name="indirectEmissions"
                step="any"
                labelText={`${t("indirectEmissions")}(tCO₂e/t)`}
                rules={setValidForm("indirectEmissions")}
                value={watch("indirectEmissions")}
                onChange={(newValue) => {
                  setValue("indirectEmissions", newValue);
                }}
              />
            </div>
            <div className="col-12 gap-2 flex-all-center">
              {tabAction === "detail" && (
                <Button
                  disabled={!isEditable}
                  type="button"
                  label={t("button.edit")}
                  size="small"
                  onClick={() => {
                    setTabAction("create");
                    setAction("update")
                  }}
                />
              )}
              {tabAction === "create" && (
                <>
                  <Button
                    disabled={!(isValid && isDirty)}
                    type="submit"
                    label={action === "create" ? t("button.create") : t("button.update")}
                    size="small"
                  />
                  {action !== "create" && (
                    <Button
                      outlined
                      size="small"
                      label={t("button.cancel")}
                      onClick={(e) => {
                        e.preventDefault();
                        clearErrors();
                        setTabAction("detail");
                        reset(oriData);
                      }}
                    />
                  )}
                </>
              )}
            </div>
          </form>
        </div>
      </div>

      <PrimeDialog
        scrollByBody={true}
        visible={showIndex}
        isShowConfBtn={false}
        onClose={() => {
          setShowIndex(false);
        }}
        onConfirm={() => {}}
        title={t("coefficient")}
        size="lg"
        bodyTemplate={
          <CarbonCoefficientComponent
            getValues={getValues}
            register={register}
            errors={errors}
            setPickDialog={setShowIndex}
            pickData={handlePickData}
          />
        }
      />
    </>
  );
}
